import { Injectable } from '@angular/core';
import { File } from './objects/file';
import {from, Observable, of, scheduled, Subject} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators'
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { User } from './objects/user';
import { Account } from './objects/account';
import * as he from 'he';
import { environment } from '../environments/environment';
import {ListItem} from "./objects/listItem";
import {colors} from "./consts/colors";
import {AppComponent} from "./app.component";

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  public SERVER_PREFIX = '';
  //
  //;static USE_MOCk = true;
  static USE_MOCk = false;

  constructor(private http: HttpClient) { }

  checkQuota(): Observable<object> {
    return this.http.get('profile/quota')
  }

  getYoutubeVideoMetaData(magic_key: string, youtubeVideoID: string): Observable<object> {
    const requestUrl = `utils/youtubemetadata`;
    return this.http.post(requestUrl, {
      youtubeVideoID: youtubeVideoID,
      magic_key: magic_key,
      gid: environment['gid']
    })
  }

  uploadChunks(fileMetaParams, blob, start, end, size, cancelObserver) {
    // cancelObserver.subscibe((a) => console.log('tyt', a))
    const headersWithoutSize: HttpHeaders = new HttpHeaders({})
    const headersWithSize: HttpHeaders = new HttpHeaders({ 'Content-Range': `bytes ${start}-${end}/${size}` })
    return this.http.post('files/upload', blob, {
      headers: size ? headersWithSize : headersWithoutSize,
      params: fileMetaParams,
      reportProgress: true,
      observe: 'events'
    })
      .pipe(takeUntil(cancelObserver));
  }

  rotateImage(params): Observable<object> {
    return this.http.post('files/rotate', params)
  }

  doLogin(login: string, password: string): Observable<object> {
    const headers: HttpHeaders = new HttpHeaders({
      'Authorization': 'Basic ' + btoa(unescape(encodeURIComponent(login + ':' + password)))
    });

    return this.http.get('session/login?auth_gid='+environment['gid'], { headers })
  }

  send2faCode(code_2fa: string, mfaType: string): Observable<object> {
    return this.http.post('session/mfa', { code: code_2fa, type: mfaType })
  }

  sendSmsCode(gid, g_hash, phone): Observable<object> {
    return this.http.post('profile/sms_login', { gid: gid, g_hash: g_hash, phone: phone })
  }

  verifySmsCode(gid, g_hash, code): Observable<object> {
    return this.http.post('profile/sms_code_verify', { gid: gid, g_hash: g_hash, code: code })
  }

  logout(): Observable<any> {
    return this.http.get('session/logout')
  }

  doAccessReset(recover_user: string): Observable<object> {
    return this.http.post('profile/password-reset', { 'recover_user': recover_user })
  }

  doPasswordReset(uuid: string, login: string, password: string): Observable<object> {
    const requestUrl = `profile/password-reset/${uuid}`;
    return this.http.post(requestUrl, { login: login, password: password })
  }

  doPasswordChange(password: string, oldPassword: string): Observable<object> {
    const requestUrl = oldPassword ? `profile/password-change` : `session/password-change`;
    const reqData = oldPassword ? { password: password, old_password: oldPassword } : { password: password };
    return this.http.post(requestUrl, reqData)
  }

  validateResetUuid(uuid: string): Observable<object> {
    const requestUrl = `profile/password-reset/${uuid}`;
    return this.http.get(requestUrl)
  }

  saveProfileSettings(settings: object): Observable<object> {
    const requestUrl = `profile/settings`;
    return this.http.post(requestUrl, settings)
  }

  updateProfilePoints(points: string, magic_key: string): Observable<object> {
    const requestUrl = `clients/updatepoints`;
    return this.http.post(requestUrl, {
      points: points,
      magic_key: magic_key,
      gid: environment['gid'],
      g_hash: environment['g_hash']
    })
  }

  getClients(path: string, magic_key: string): Observable<File[]> {
    return this.http.post('clients/list', {
      path: path ? path.replace(/\/+/g, '/') : '/',
      magic_key: magic_key,
      gid: environment['gid']
    }).pipe(map(res => this.extractProfilesData(res, 'client')));
  }

  getGroupTrainers(keep_hidden_trainers = false): Observable<ListItem[]> {
    return this.http.post('group/trainers', {
      gid: environment['gid'],
      g_hash: environment['g_hash'],
      keep_hidden_trainers: keep_hidden_trainers
    }).pipe(map(res => this.extractProfilesData(res, 'trainer')));
  }

  getBookingData(from_date,to_date,trainer_id = 0,load_playhour_data = false): Observable<object[]> {
    let params = {
      from_date: from_date,
      to_date: to_date,
      trainer_id: trainer_id,
      load_playhour_data: load_playhour_data,
      gid: environment['gid'],
      g_hash: environment['g_hash']
    }
    return this.http.post('group/bookingdata', params).pipe(map(res => this.extractBookingData(res,params)));
  }
  public extractBookingData(res: any,params: any = null) {
    if (!res) {
      return []
    }
    return res.map(data => {
      //if(data['id'] == '28D93CBC-6DDB-4051-964D-20154B648295') console.log(data);
      let eventLabel = (params?.trainer_id) ? (data['isBusy'] ? '🎓'+data['playHour'] : data['playHour']) : (data['title'] ? data['title'] : data['playHour']);
      let bookingEvent = {
        id: data['id'],
        resourceId: data['resourceId'],
        title: eventLabel,
        start: data['start'],
        end: data['end'],
        allDay: false,
        backgroundColor: colors[data['backgroundColor']],
        textColor: data['textColor'] ? data['textColor'] : '#fff',
        borderColor: 'transparent',
        extendedProps: data['extendedProps']
      }
      return bookingEvent
    })
  }

  getGroupPackages(): Observable<object> {
    return this.http.post('group/get_packages', {
      gid: environment['gid'],
      g_hash: environment['g_hash']
    });
  }
  saveProfilePurchase(profile_id: number, purchase_data: object, action: string): Observable<object> {
    return this.http.post('profile/save_purchase', {
      profile_id: profile_id,
      purchase_data: purchase_data,
      action: action,
      gid: environment['gid'],
      g_hash: environment['g_hash']
    }).pipe(map(res => this.extractProfilePurchases(res)));
  }
  getProfilePurchases(profile_id: number): Observable<object> {
    return this.http.post('profile/purchases', {
      profile_id: profile_id,
      gid: environment['gid'],
      g_hash: environment['g_hash']
    }).pipe(map(res => this.extractProfilePurchases(res)));
  }
  getProfileProposals(profile_id: number): Observable<object> {
    return this.http.post('profile/proposals', {
      profile_id: profile_id,
      gid: environment['gid'],
      g_hash: environment['g_hash']
    }).pipe(map(res => this.extractProfilePurchases(res)));
  }
  public extractProfilePurchases(res: any) {
    if (!res) {
      return []
    }
    return res.map((data) => {
      let name = data.package_name;
      let filter_string = data.package_name;
      let type = 'profile_purchase';
      let time = data.created_time * 1000;
      let size = 0;
      let path = '/'+data.purchase_id;
      let icon = '';
      let ava = '';
      let preview = '';
      return ListItem.genItem({ name, type, time, size, path, icon, ava, preview, filter_string, data })
    })
  }

  getStaticArticle(staticAlias: string, magic_key: string): Observable<object> {
    return this.http.post('article/item', {
      alias: staticAlias,
      magic_key: magic_key,
      gid: environment['gid']
    }).pipe(map(res => this.extractArticleItemData(res)));
  }

  getArticleItem(uuid: string, magic_key: string): Observable<object> {
    return this.http.post('article/item', {
      article_uuid: uuid,
      magic_key: magic_key,
      gid: environment['gid']
    }).pipe(map(res => this.extractArticleItemData(res)));
  }

  exportArticleItem(uuid: string, folder_uuid: string, alias: string): Observable<object> {
    return this.http.post('article/item_export', {
      article_uuid: uuid,
      folder_uuid: folder_uuid,
      alias: alias,
      gid: environment['gid']
    }).pipe(map(res => this.extractArticleItemData(res)));
  }

  getArticleCourse(folderUUID: string, articleUUID: string, magic_key: string): Observable<object> {
    return this.http.post('article/item', {
      folder_uuid: folderUUID,
      article_uuid: articleUUID,
      magic_key: magic_key,
      gid: environment['gid']
    }).pipe(map(res => this.extractArticleItemData(res)));
  }

  getArticlesFolders(magic_key: string): Observable<object[]> {
    return this.http.post('articles/folders', {
      magic_key: magic_key,
      gid: environment['gid']
    }).pipe(map(res => this.extractArticlesFolders(res)));
  }

  getArticlesByFolder(magic_key: string, folder_uuid: string): Observable<object> {
    return this.http.post('articles/byfolder', {
      magic_key: magic_key,
      folder_uuid: folder_uuid,
      gid: environment['gid']
    }).pipe(map(res => this.extractArticlesByFolder(res)));
  }

  updateArticlesGroups(data: object, folder_uuid: string): Observable<object> {
    const requestUrl = `articles/updatearticlesgroups`;
    return this.http.post(requestUrl, {
      articles_groups: data,
      folder_uuid: folder_uuid,
      gid: environment['gid']
    })
  }

  insertArticleItem(magic_key: string, article_uuid: string, categoryIdx: number, folder_uuid: string, data: object): Observable<object> {
    const requestUrl = `articles/insertarticleitem`;
    return this.http.post(requestUrl, {
      magic_key: magic_key,
      article: data,
      categoryIdx: categoryIdx,
      article_uuid: article_uuid,
      folder_uuid: folder_uuid,
      gid: environment['gid']
    })
  }

  updateArticleItem(magic_key: string, article_uuid: string, folder_uuid: string, data: object): Observable<object> {
    const requestUrl = `articles/updatearticleitem`;
    return this.http.post(requestUrl, {
      magic_key: magic_key,
      article: data,
      article_uuid: article_uuid,
      folder_uuid: folder_uuid,
      gid: environment['gid']
    })
  }

  insertFolderItem(magic_key: string, data: object): Observable<object> {
    const requestUrl = `articles/insertfolder`;
    return this.http.post(requestUrl, {
      magic_key: magic_key,
      folder: data,
      gid: environment['gid']
    })
  }
  updateFolderItem(magic_key: string, data: object): Observable<object> {
    const requestUrl = `articles/updatefolder`;
    return this.http.post(requestUrl, {
      magic_key: magic_key,
      folder: data,
      gid: environment['gid']
    })
  }
  deleteFolder(magic_key: string, folderUUID: string): Observable<object> {
    const requestUrl = `articles/deletefolder`;
    return this.http.post(requestUrl, {
      magic_key: magic_key,
      folder_uuid: folderUUID,
      gid: environment['gid']
    })
  }

  shareCourse(magic_key: string, action: string, folder_uuid: string): Observable<object> {
    const requestUrl = `client/sharecourse`;
    return this.http.post(requestUrl, {
      magic_key: magic_key,
      action: action,
      folder_uuid: folder_uuid,
      gid: environment['gid']
    })
  }

  getLiqPayCheckoutParams(params: object, magic_key: string): Observable<object> {
    let postParams = Object.assign({magic_key: magic_key}, params);
    return this.http.post('booking/liqpayprepare', postParams);
  }

  checkLiqPaySlot(params: object, magic_key: string): Observable<object> {
    let postParams = Object.assign({magic_key: magic_key}, params);
    return this.http.post('booking/liqpaycheckslot', postParams);
  }

  getSnapshotsList(): Observable<File[]> {
    return this.http.get('snapshots')
      .pipe(map(res => this.extractSnapshotListData(res)));
  }

  getSnapshotFiles(snapshot: string, path = '/'): Observable<File[]> {
    path = path || '/'
    return this.http.post('snapshots/list', { snapshot: snapshot, directory: path.replace(/\/+/g, '/') })
      .pipe(map(res => this.extractSnapshotFilesData(res, snapshot, path)));
  }

  getPublicFiles(path: string, auth: string): Observable<File[]> {
    return this.http.post('public/list', { 'path': path ? path.replace(/\/+/g, '/') : '/' })
      .pipe(map(res => this.extractProfilesData(res, path)));
  }

  deleteFiles(files: File[]): Observable<object> {
    const reqData = { files: files.map(file => file.full_path.replace(/\/+/g, '/')) };
    return this.http.post('files/delete', reqData)
  }

  makeDir(path: string): Observable<object> {
    const reqData = { 'path': path.replace(/\/+/g, '/') };
    return this.http.post('files/mkdir', reqData)
  }

  copyFiles(files: File[], path: string): Observable<any> {
    const reqData = { path: path, files: [] };
    reqData.files = files.map(file => file.full_path.replace(/\/+/g, '/'))
    return this.http.post('files/copy', reqData)
  }
  moveFiles(files: File[], path: string): Observable<any> {
    const reqData = { overwrite: true, path: path.replace(/\/+/g, '/'), files: [] };
    reqData.files = files.map(file => file.full_path.replace(/\/+/g, '/'));
    return this.http.post('files/move', reqData)
  }
  restoreFiles(files: File[]): Observable<any> {
    const filePathes = [];
    for (let i = 0; i < files.length; i++) {
      const fromParts = files[i].full_path_old.replace(/\/+/g, '/').replace(/^\/+/, '').split('/');
      filePathes.push({
        'snapshot': fromParts.shift(),
        'from': '/' + fromParts.join('/'),
        'to': files[i].full_path.replace(/\/+/g, '/')
      })
    }
    return this.http.post('snapshots/restore', filePathes)
  }
  zipFiles(zipPath: string, files: File[]): Observable<any> {
    let requestPath: string = zipPath.replace(/\/+/g, '/');
    const parentFolder = requestPath.split('/').reverse().slice(1).reverse().join('/'); // remove last elem
    const zipData = { path: requestPath, files: [] };
    for (let i = 0; i < files.length; i++) {
      requestPath = files[i].full_path.replace(/\/+/g, '/');
      zipData.files.push({
        'real_path': requestPath,
        'user_path': (requestPath.indexOf(parentFolder) === 0) ?
          requestPath.substr(parentFolder.length, requestPath.length - parentFolder.length) : requestPath,
      })
    }
    return this.http.post('files/zip', zipData)
  }

  getDirectLink(fileName: string, files: File[]): Observable<any> {
    const reqData = { 'download_file_name': fileName, files: files.map(file => file.full_path.replace(/\/+/g, '/')) };
    return this.http.post('files/zero', reqData)
  }

  getDownloadLink(targetFiles: File[]): any {
    const files = targetFiles.map(file => file.full_path.replace(/\/+/g, '/'))
    return this.http.post('files/download', { files })
  }


  renameFile({ full_path, full_path_old }): Observable<any> {
    const data = { path_from: full_path_old.replace(/\/+/g, '/'), path_to: full_path.replace(/\/+/g, '/') }

    return this.http.post('files/rename', data)
  }

  unzipArchive(file: File): Observable<any> {
    const path: string = file.full_path.replace(/\/+/g, '/')
    return this.http.post('files/unzip', { path })
  }

  getUser(uuid = null,magic_hash = null): Observable<User> {
    if (ApiService.USE_MOCk) {
      let profile = this.extractUser(JSON.parse('{"id":1167,"magic_key":"95E177C2-70BC-4F73-A2EE-3CAB159E011F","wmk":null,"sex":"M","birthtime":283780800,"saved_time":1635290434,"visitor_time":1635290434,"profile_age":43,"role_id":1,"name":"39_95e177c2-70bc-4f73-a2ee-3cab159e011f@tmp.com","password_md5":null,"cname":null,"registered":"N","fetched":"N","locale":0,"location":null,"country_id":0,"city_id":0,"shipping_name":"Alex Shulak","shipping_surname":"ddsfsd","shipping_country":null,"shipping_country_code":null,"shipping_state":null,"shipping_city":null,"shipping_address":null,"shipping_zip":null,"shipping_email":null,"shipping_phone":"380969533903","shipping_state_code":null,"fb_location":null,"created_time":1635290434,"lang":"en","privacy_profile":"public","privacy_meals":"public","privacy_workouts":"public","privacy_measures":"public","privacy_events":"public","picture_src_icon":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd1i.png","picture_src_ava":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png","picture_src_preview":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkdio.png","profile_programs":null,"profile_measures":null,"fetched_email":null,"about":null,"color":16777215,"branding_bg_image_src":null,"resources":"{}","transformations":[],"transformation_progres":0,"points":0,"points_period":0,"rank":1000,"bonus_points":0,"help_data":"{}","show_help":"Y","ack_tcs":"Y","ack_privacy":"Y","native_group":{"id":39,"label":null,"description":"SquashFit gym","privacy":"public","type":"default","name":"SquashFit","profile_id":345,"membership":"public","status":"trainer","picture_src_ava":"filesstore\\/g_835f1369550e0cddaecaf687636653b6_preview.png","picture_src_icon":"filesstore\\/g_835f1369550e0cddaecaf687636653b6_preview.png","picture_src_preview":"filesstore\\/g_835f1369550e0cddaecaf687636653b6_preview.png","members_qty":0,"transformations_qty":0,"events_qty":0,"recommended_followings":[],"sig":"c43742eb95c797348b21b04115661a48"},"payload":"{\\"show_all_trainers\\":true}","classes":[{"id":"565","name":"ogg","description":null,"personal":"C","active":"Y","uuid":"B27C4750-7281-4253-9857-1E58836A64FA","picture_src_ava":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p345\\/g_3BY5wc9l_wEr3q3v84m.png","picture_src_icon":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p345\\/g_3BY5wc9l_wEr3q3v81i.png","picture_src_preview":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p345\\/g_3BY5wc9l_wEr3q3v8io.png","configuration":"{\\"type\\":\\"user_class\\",\\"privacy\\":\\"confirmation\\"}","ctime":"1638843673","profile_id":"345","size":"2","class_profile_privacys":"{private,private}","class_profile_memberships":"{pending,active}","class_profile_ids":"{1167,345}","class_profile_points":"{0,603}","class_profile_ranks":"{1000,1000}","class_profile_sexs":"{M,M}","class_profile_shipping_names":"{\\"Alex Shulak\\",\\"\u0430\u0441\u0438\u0441\u0442\u0435\u043d\u0442 Alex\\"}","class_profile_picture_src_avas":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png,http:\\/\\/bufffit.com\\/filesstore\\/p345\\/g_9Eb3D89l_qAq5jblx4m.png?t=1581432572&t=1581540411&t=1581770791&t=1581787565&t=1581788050&t=1584215289&t=1586005689&t=1589533425&t=1589734966&t=1595315557&t=1595323112&t=1595692783&t=1597941933&t=1604553316&t=1605288736&t=1616170944&t=1617376960&t=1617381759&t=1619642740&t=1619912662&t=1621085507&t=1621086696&t=1621086722&t=1621399176}","class_profile_picture_src_icons":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd1i.png,http:\\/\\/bufffit.com\\/filesstore\\/p345\\/g_9Eb3D89l_qAq5jblx1i.png?t=1581432572&t=1581540411&t=1581770791&t=1581787565&t=1581788050&t=1584215289&t=1586005689&t=1589533425&t=1589734966&t=1595315557&t=1595323112&t=1595692783&t=1597941933&t=1604553316&t=1605288736&t=1616170944&t=1617376960&t=1617381759&t=1619642740&t=1619912662&t=1621085507&t=1621086696&t=1621086722&t=1621399176}","class_profile_picture_src_previews":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkdio.png,http:\\/\\/bufffit.com\\/filesstore\\/p345\\/g_9Eb3D89l_qAq5jblxio.png?t=1581432572&t=1581540411&t=1581770791&t=1581787565&t=1581788050&t=1584215289&t=1586005689&t=1589533425&t=1589734966&t=1595315557&t=1595323112&t=1595692783&t=1597941933&t=1604553316&t=1605288736&t=1616170944&t=1617376960&t=1617381759&t=1619642740&t=1619912662&t=1621085507&t=1621086696&t=1621086722&t=1621399176}","profiles":[{"id":"1167","points":"0","rank":"1000","sex":"M","shipping_name":"Alex Shulak","picture_src_ava":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png","picture_src_icon":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png","picture_src_preview":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkdio.png","privacy":"private","membership":"pending"},{"id":"345","points":"603","rank":"1000","sex":"M","shipping_name":"\u0430\u0441\u0438\u0441\u0442\u0435\u043d\u0442 Alex","picture_src_ava":"http:\\/\\/bufffit.com\\/filesstore\\/p345\\/g_9Eb3D89l_qAq5jblx4m.png?t=1581432572&t=1581540411&t=1581770791&t=1581787565&t=1581788050&t=1584215289&t=1586005689&t=1589533425&t=1589734966&t=1595315557&t=1595323112&t=1595692783&t=1597941933&t=1604553316&t=1605288736&t=1616170944&t=1617376960&t=1617381759&t=1619642740&t=1619912662&t=1621085507&t=1621086696&t=1621086722&t=1621399176","picture_src_icon":"http:\\/\\/bufffit.com\\/filesstore\\/p345\\/g_9Eb3D89l_qAq5jblx4m.png?t=1581432572&t=1581540411&t=1581770791&t=1581787565&t=1581788050&t=1584215289&t=1586005689&t=1589533425&t=1589734966&t=1595315557&t=1595323112&t=1595692783&t=1597941933&t=1604553316&t=1605288736&t=1616170944&t=1617376960&t=1617381759&t=1619642740&t=1619912662&t=1621085507&t=1621086696&t=1621086722&t=1621399176","picture_src_preview":"http:\\/\\/bufffit.com\\/filesstore\\/p345\\/g_9Eb3D89l_qAq5jblxio.png?t=1581432572&t=1581540411&t=1581770791&t=1581787565&t=1581788050&t=1584215289&t=1586005689&t=1589533425&t=1589734966&t=1595315557&t=1595323112&t=1595692783&t=1597941933&t=1604553316&t=1605288736&t=1616170944&t=1617376960&t=1617381759&t=1619642740&t=1619912662&t=1621085507&t=1621086696&t=1621086722&t=1621399176","privacy":"private","membership":"active"}],"sig":"ba63d2d27202cf99581065d90aca6be7"},{"id":"571","name":"Group1 1167","description":null,"personal":"C","active":"Y","uuid":"29CF7CAF-6630-4101-A6D0-406871CD3FC8","picture_src_ava":"\\/img\\/squash\\/squashfitIcon_400x267.png","picture_src_icon":"\\/img\\/squash\\/squashfitIcon_400x267.png","picture_src_preview":"\\/img\\/squash\\/squashfitIcon_400x267.png","configuration":"{\\"type\\":\\"user_class\\",\\"privacy\\":\\"confirmation\\"}","ctime":"1640181214","profile_id":"1167","size":"2","class_profile_privacys":"{private,private}","class_profile_memberships":"{active,active}","class_profile_ids":"{1167,1738}","class_profile_points":"{0,0}","class_profile_ranks":"{1000,1000}","class_profile_sexs":"{M,M}","class_profile_shipping_names":"{\\"Alex Shulak\\",\\"Alex Vodafone\\"}","class_profile_picture_src_avas":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png,https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png}","class_profile_picture_src_icons":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd1i.png,https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png}","class_profile_picture_src_previews":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkdio.png,https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png}","profiles":[{"id":"1167","points":"0","rank":"1000","sex":"M","shipping_name":"Alex Shulak","picture_src_ava":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png","picture_src_icon":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png","picture_src_preview":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkdio.png","privacy":"private","membership":"active"},{"id":"1738","points":"0","rank":"1000","sex":"M","shipping_name":"Alex Vodafone","picture_src_ava":"https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png","picture_src_icon":"https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png","picture_src_preview":"https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png","privacy":"private","membership":"active"}],"sig":"01df83bf0e2f965902bdb366b1ac2a9a"},{"id":"572","name":"Group2 1167","description":null,"personal":"C","active":"Y","uuid":"78832DA9-8811-4EDC-A2C5-93BA98D59152","picture_src_ava":"\\/img\\/squash\\/squashfitIcon_400x267.png","picture_src_icon":"\\/img\\/squash\\/squashfitIcon_400x267.png","picture_src_preview":"\\/img\\/squash\\/squashfitIcon_400x267.png","configuration":"{\\"type\\":\\"user_class\\",\\"privacy\\":\\"confirmation\\",\\"link\\":\\"https:\\/\\/my.squashcenter.com.ua\\/ua\\/articles\\/C08054D6-04D1-41C0-84FA-61852203F5BD\\/bonuses_squashfit.html\\"}","ctime":"1640184398","profile_id":"1167","size":"3","class_profile_privacys":"{private,private,private}","class_profile_memberships":"{active,active,active}","class_profile_ids":"{1167,1153,1738}","class_profile_points":"{0,1,0}","class_profile_ranks":"{1000,1658,1000}","class_profile_sexs":"{M,M,M}","class_profile_shipping_names":"{\\"Alex Shulak\\",\\"\u0412\u0430\u0441\u0438\u043b\u044c \u0411\u0435\u0441\u0441\u0430\u0440\u0430\u0431\u043e\u0432\\",\\"Alex Vodafone\\"}","class_profile_picture_src_avas":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png,http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png,https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png}","class_profile_picture_src_icons":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd1i.png,http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png,https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png}","class_profile_picture_src_previews":"{http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkdio.png,http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png,https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png}","profiles":[{"id":"1167","points":"0","rank":"1000","sex":"M","shipping_name":"Alex Shulak","picture_src_ava":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png","picture_src_icon":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkd4m.png","picture_src_preview":"http:\\/\\/devsquashfit.alex-bufffit.dev.home:800\\/filesstore\\/p1167\\/g_yffyQdwf_46r6oqkdio.png","privacy":"private","membership":"active"},{"id":"1153","points":"1","rank":"1658","sex":"M","shipping_name":"\u0412\u0430\u0441\u0438\u043b\u044c \u0411\u0435\u0441\u0441\u0430\u0440\u0430\u0431\u043e\u0432","picture_src_ava":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png","picture_src_icon":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png","picture_src_preview":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png","privacy":"private","membership":"active"},{"id":"1738","points":"0","rank":"1000","sex":"M","shipping_name":"Alex Vodafone","picture_src_ava":"https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png","picture_src_icon":"https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png","picture_src_preview":"https:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_mJr3yz2v.png","privacy":"private","membership":"active"}],"sig":"62e3654bc57a0ac97c66ceda598ef3fa"}],"friends":[{"id":2061,"magic_key":"","wmk":"","sex":"M","birthtime":0,"saved_time":0,"visitor_time":0,"profile_age":0,"role_id":0,"name":"","password_md5":"","cname":null,"registered":"","fetched":"","locale":0,"location":"","country_id":0,"city_id":0,"shipping_name":"\u0414\u043c\u0438\u0442\u0440\u043e \u0428\u043d\u0435\u0439\u0434\u0435\u0440","shipping_surname":"","shipping_country":"","shipping_country_code":"","shipping_state":"","shipping_city":"","shipping_address":"","shipping_zip":"","shipping_email":"","shipping_phone":null,"shipping_state_code":"","fb_location":"","created_time":0,"lang":"","privacy_profile":"","privacy_meals":"","privacy_workouts":"","privacy_measures":"","privacy_events":"","picture_src_icon":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_anqxzimo.png","picture_src_ava":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_anqxzimo.png","picture_src_preview":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_anqxzimo.png","profile_programs":[],"profile_measures":[],"fetched_email":"","about":"","color":16777215,"branding_bg_image_src":"","resources":"","transformations":[],"transformation_progres":0,"points":0,"points_period":0,"rank":1618,"bonus_points":0,"help_data":"{}","show_help":"","ack_tcs":"","ack_privacy":"","native_group":null,"payload":null,"classes":[],"friends":[],"usage":null,"my_following":false,"measure_type":null,"sig":"212db592ed300f748759fdb1a6694525","relation_params":{"type":"friends","status":"active"},"do_key":null},{"id":1153,"magic_key":"","wmk":"","sex":"M","birthtime":0,"saved_time":0,"visitor_time":0,"profile_age":0,"role_id":0,"name":"","password_md5":"","cname":null,"registered":"","fetched":"","locale":0,"location":"","country_id":0,"city_id":0,"shipping_name":"\u0412\u0430\u0441\u0438\u043b\u044c \u0411\u0435\u0441\u0441\u0430\u0440\u0430\u0431\u043e\u0432","shipping_surname":"","shipping_country":"","shipping_country_code":"","shipping_state":"","shipping_city":"","shipping_address":"","shipping_zip":"","shipping_email":"","shipping_phone":null,"shipping_state_code":"","fb_location":"","created_time":0,"lang":"","privacy_profile":"","privacy_meals":"","privacy_workouts":"","privacy_measures":"","privacy_events":"","picture_src_icon":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png","picture_src_ava":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png","picture_src_preview":"http:\\/\\/bufffit.com\\/filesstore\\/39\\/players\\/p_raw_6kqz2ks2.png","profile_programs":[],"profile_measures":[],"fetched_email":"","about":"","color":16777215,"branding_bg_image_src":"","resources":"","transformations":[],"transformation_progres":0,"points":1,"points_period":0,"rank":1658,"bonus_points":0,"help_data":"{}","show_help":"","ack_tcs":"","ack_privacy":"","native_group":null,"payload":"{\\"trainer_sort_order\\":10}","classes":[],"friends":[],"usage":null,"my_following":false,"measure_type":null,"sig":"47651bd71d1da0a584a2afdc99bee464","relation_params":{"type":"friends","status":"active"},"do_key":null}],"usage":null,"my_following":null,"measure_type":"lbs","sig":"6d5324af20474a17b0f5e15c7f1c9e76"}'));
      console.log('profile mocked',profile);
      return of(profile)
    }

    const params = {gid: environment['gid'],g_hash: environment['g_hash']};
    if(uuid && magic_hash) { //magic authentication
      params['uuid'] = uuid;
      params['magic_hash'] = magic_hash;
    }
    return this.http.post('profile', params)
      .pipe(map(res => this.extractUser(res)));
  }

  updateProfile(params: object): Observable<object> {
    return this.http.post('profile/update', params);
  }
  updateProfileInternal(params: object): Observable<object> {
    return this.http.post('profile/update_internal', params);
  }
  updateProfilePayload(params: object): Observable<object> {
    return this.http.post('profile/update_payload', params);
  }

  setupMfa(mfaType: string, phone: string = ''): Observable<object> {
    if (mfaType === 'SMS') {
      return this.http.post('session/setup-mfa', { type: mfaType, phone: phone })
    } else {
      return this.http.post('session/setup-mfa', { type: mfaType })
    }

  }

  getJobs(jobs: string[]): Observable<any> {
    return this.http.post('jobs/list', { jobs })
  }

  getJobInfo(jobId: string): Observable<any> {
    return this.http.get(`jobs/${jobId}`)
  }

  private extractUser(res: any) {
    const user: User = res as User;
    // user._auth_code = auth;
    return user;
  }

  getAccount(gid, g_hash, liqpay_params = null): Observable<Account> {
    if (ApiService.USE_MOCk) {
      let account = this.extractAccount(JSON.parse('{"id":39,"description":"SquashFit gym","privacy":"public","type":"default","name":"Squash","profile_id":345,"membership":"public","status":null,"picture_src_ava":"filesstore\\/g_835f1369550e0cddaecaf687636653b6_preview.png","picture_src_icon":"filesstore\\/g_835f1369550e0cddaecaf687636653b6_preview.png","picture_src_preview":"filesstore\\/g_835f1369550e0cddaecaf687636653b6_preview.png","recommended_followings":null,"sig":"c43742eb95c797348b21b04115661a48"}'));
      console.log('account mocked',account);
      return of(account)
      //return from([account])
    }
    return this.http.post('account', { gid: gid, g_hash: g_hash, liqpay_params: liqpay_params })
      .pipe(map(res => this.extractAccount(res)));
  }

  getGroupEvents(gid, g_hash, events_filter): Observable<object> {
    return this.http.post('group/events', {
      gid: gid,
      g_hash: g_hash,
      events_filter: events_filter
    }).pipe(map(res => this.extractClubEventsData(res)));
  }
  getCommunityClasses(filter, gid, g_hash, pid = 0): Observable<object> {
    return this.http.post('group/community_classes', {
      filter: filter,
      gid: gid,
      g_hash: g_hash,
      pid: pid
    }).pipe(map(res => this.extractCommunityClasses(res)));
  }
  getProfileEvents(gid, profile_id, g_hash, action_type): Observable<object> {
    return this.http.post('group/profile_events', {
      gid: gid,
      profile_id: profile_id,
      g_hash: g_hash,
      action_type: action_type
    });
  }
  getClassEvents(gid, class_id, g_hash): Observable<object> {
    return this.http.post('group/class_events', {
      gid: gid,
      class_id: class_id,
      g_hash: g_hash
    });
  }
  // saveClassMembership(class_id, profile_id, action): Observable<object> {
  //   return this.http.post('group/class_membership', {
  //     gid: environment['gid'],
  //     g_hash: environment['g_hash'],
  //     class_id: class_id,
  //     profile_id: profile_id,
  //     action: action
  //   });
  // }
  getCupDetails(gid, g_hash, class_id): Observable<object> {
    return this.http.post('group/cup_details', {
      gid: gid,
      g_hash: g_hash,
      class_id: class_id
    });
  }
  packageUsageCheck(gid, g_hash, class_uuid, id, action_type, event_type, configToCheck): Observable<object> {
    return this.http.post('group/package_usage_check', {
      gid: gid,
      g_hash: g_hash,
      class_uuid: class_uuid,
      id: id,
      action_type: action_type,
      event_type: event_type,
      configToCheck: configToCheck,
    });
  }
  updateClassItem(gid, g_hash, id, configToSave): Observable<object> {
    return this.http.post('group/save_class', {
      gid: gid,
      g_hash: g_hash,
      id: id,
      configToSave: configToSave,
    });
  }
  deleteClassItem(gid, g_hash, id): Observable<object> {
    return this.http.post('group/save_class', {
      gid: gid,
      g_hash: g_hash,
      id: id,
      delete: 'Y',
    });
  }
  relationConfirmation(gid, g_hash, pid, cid, action): Observable<object> {
    return this.http.post('group/relation_confirmation', {
      gid: gid,
      g_hash: g_hash,
      pid: pid,
      cid: cid,
      action: action,
    });
  }
  saveUserReferral(gid, g_hash, sid, tid, action): Observable<object> {
    return this.http.post('group/save_referral', {
      gid: gid,
      g_hash: g_hash,
      sid: sid,
      tid: tid,
      action: action,
    });
  }
  saveGame(gid, g_hash, cid, class_uuid, payload): Observable<object> {
    return this.http.post('group/save_game', {
      gid: gid,
      g_hash: g_hash,
      cid: cid,
      class_uuid: class_uuid,
      payload: payload,
    });
  }
  cupGameAction(gid, g_hash, cid, payload, action, game_id = 0): Observable<object> {
    return this.http.post('group/cup_game', {
      gid: gid,
      g_hash: g_hash,
      cid: cid,
      payload: payload,
      action: action,
      game_id: game_id
    });
  }
  updateEventItem(gid, g_hash, class_uuid, id, action_type, event_type, configToSave): Observable<object> {
    return this.http.post('group/save_event', {
      gid: gid,
      g_hash: g_hash,
      class_uuid: class_uuid,
      id: id,
      action_type: action_type,
      event_type: event_type,
      configToSave: configToSave,
    });
  }
  updateEvent(gid, g_hash, event, changeAction): Observable<object> {
    return this.http.post('group/update_event', {
      gid: gid,
      g_hash: g_hash,
      event: event,
      change_action: changeAction,
    });
  }
  deleteEvent(gid, g_hash, event): Observable<object> {
    return this.http.post('group/delete_event', {
      gid: gid,
      g_hash: g_hash,
      event: event,
    });
  }
  getPackageDetails(gid, g_hash, for_trainers = false): Observable<object> {
    return this.http.post('group/package_details', {
      gid: gid,
      g_hash: g_hash
    }).pipe(map(res => this.extractPackages(res, for_trainers)));
  }
  searchUserByPhone(gid, g_hash, phone): Observable<object> {
    return this.http.post('group/search_user', {
      gid: gid,
      g_hash: g_hash,
      phone: phone,
    });
  }

  extractPackages(res: any, for_trainers: boolean, non_trainers: boolean = false) {
    if (!res || !res['packages']) {
      return {'packages':[]}
    }
    // return res.map((obj) => {
    //   const user: User = obj as User;
    //   return user;
    // });
    if(for_trainers) {
      let packagesFiltered = res['packages'].filter( packageItem => {
        let hasTrainingServices = false;
        if(packageItem['services']) packageItem['services'].forEach(service => {
          if(service['params']['unit'] == 'training') hasTrainingServices = true;
        })
        return hasTrainingServices;
      })
      return {'packages':packagesFiltered}
    } else if(non_trainers) {
      let packagesFiltered = res['packages'].filter( packageItem => {
        let hasTrainingServices = false;
        if(packageItem['services']) packageItem['services'].forEach(service => {
          if(service['params']['unit'] == 'training') hasTrainingServices = true;
        })
        return !hasTrainingServices;
      })
      return {'packages':packagesFiltered}
    } else {
      return res
    }
  }

  private extractAccount(res: any) {
    if(res.payload_parsed['gid']) environment['gid'] = res.payload_parsed['gid'];
    if(res.payload_parsed['g_hash']) environment['g_hash'] = res.payload_parsed['g_hash'];
    if(res.payload_parsed['ui_conf']['brandingTheme']) environment['brandingTheme'] = res.payload_parsed['ui_conf']['brandingTheme'];
    if(res.payload_parsed['ui_conf']['powered_by_company_url']) environment['powered_by_company_url'] = res.payload_parsed['ui_conf']['powered_by_company_url'];
    if(res.payload_parsed['ui_conf']['powered_by_company']) environment['powered_by_company'] = res.payload_parsed['ui_conf']['powered_by_company'];
    if(res.payload_parsed['ui_conf']['mobile_exit_strategy']) environment['mobile_exit_strategy'] = res.payload_parsed['ui_conf']['mobile_exit_strategy'];
    if(res.payload_parsed['ui_conf']['viber_url']) environment['viber_url'] = res.payload_parsed['ui_conf']['viber_url'];
    const account: Account = new Account
    account.id = 1;
    account.label = res.name;
    account.logo = res.picture_src_ava ?
      (res.picture_src_ava.match(/^http/i) ? res.picture_src_ava : environment['logo_server'] + res.picture_src_ava) : '';
    account.public_upload = 'N';
    account.public_download = 'N';
    account.ack_tos = 'N';
    account.tos_prompt = 'N';
    account.tos_message = '';
    account.show_tos = 'N';
    account.back_link = 'Y';
    account.active = 'Y';
    account.customisable_settings = null;
    account.payload_parsed = res.payload_parsed;
    if(res.calendars) account.calendars = res.calendars;
    return account;
  }

  private checkTosOption(show_tos: string, tos_message: string): string {
    if ( (show_tos === 'E' || show_tos === 'B') && tos_message) {
      return 'Y';
    }
    return 'N';
  }

  public extractProfilesData(res: any, defaultType: string = 'client') {
    if (!res) {
      return []
    }
    // return res.map((obj) => {
    //   const user: User = obj as User;
    //   return user;
    // });
    return res.map((data, idx, fullArr) => {
      let name = data.shipping_name;
      let filter_string = data.shipping_name + '#' + data.shipping_phone + '#' + (data.classes ? ' '+data.classes : '');
      filter_string = filter_string.toLowerCase()
      let type = defaultType;
      let time = data.created_time * 1000;
      let size = 0;
      let path = '/'+data.id+'_'+(data.native_group?.status ? data.native_group?.status : '');
      let icon = data.picture_src_icon;
      let ava = data.picture_src_ava;
      let preview = data.picture_src_preview;
      if(data['play_conf_weekdays']) {
        data['play_conf_weekdays_formatted'] = data['play_conf_weekdays'].split('|')
          .map((label_key) => AppComponent.getLabel(label_key)).join('/');
      }
      if(data['play_conf_weekends']) {
        data['play_conf_weekends_formatted'] = data['play_conf_weekends'].split('|')
          .map((label_key) => AppComponent.getLabel(label_key)).join('/');
      }
      if(defaultType == 'climb_cup_player') {
        data['list_class'] = '';
        if(data.call_game_description == 'player_available') {
          if(fullArr[idx+1]?.call_game_description == 'player_available') {
            data['list_class'] = 'player-row-upper border-color-green';
          } else {
            data['list_class'] = 'player-row-single border-color-green';
          }
        } else if(data.call_game_description == 'self_skip' || AppComponent.user.id == data['id']) {
          //data['list_class'] = 'player-row-single border-color-primary';
          data['list_class'] = 'player-row-single border-color-accent';
        } else if(data.call_game_description.match(/^mutual_/)) {
          data['list_class'] = 'player-row-single border-color-warn';
        }
      }
      return ListItem.genItem({ name, type, time, size, path, icon, ava, preview, filter_string, data })
    })
  }

  public extractClubEventsData(res: any) {
    if (!res) {
      return []
    }
    return res.map((data) => {
      const payload = data.payload ? JSON.parse(data.payload) : new Object();
      const parent_payload = data.parent_payload ? JSON.parse(data.parent_payload) : new Object();
      let name = data['label'];
      let filter_string = payload['title']; //todo
      let type = payload['type'];
      let time = data['daytime'] * 1000;
      let size = 0;
      let path = '/'+data['id'];
      let icon = data['profileData']['picture_src_icon'];
      let ava = data['profileData']['picture_src_ava'];
      let preview = data['profileData']['picture_src_preview'];
      data['payload_parsed'] = payload;
      data['parent_payload_parsed'] = parent_payload;
      return ListItem.genItem({ name, type, time, size, path, icon, ava, preview, filter_string, data })
    })
  }
  public extractCommunityClasses(res: any) {
    if (!res) {
      return []
    }
    return res.map((data) => {
      const config = JSON.parse(data['configuration']);
      let name = data['name'];
      let filter_string = data['name']; //todo
      let type = 'subscription_class';
      let time = data['ctime'] * 1000;
      let size = data['size'];
      let path = '/'+data['id'];
      let icon = data['picture_src_icon'];
      let ava = data['picture_src_ava'];
      let preview = data['picture_src_preview'];
      data['payload_parsed'] = config ? config : new Object();
      return ListItem.genItem({ name, type, time, size, path, icon, ava, preview, filter_string, data })
    })
  }

  private extractArticlesFolders(res: any) {
    if (!res) {
      return []
    }
    // return res.map((obj) => {
    //   const user: User = obj as User;
    //   return user;
    // });
    let ret = res['articlesFolders'];
    return ret.map((data) => {
      let name = data.name;
      let filter_string = data.name;
      let type = 'article_folder';
      let time = data.created_time * 1000;
      let size = data.size;
      let path = '/'+data.uuid;
      let icon = data.picture_src_icon;
      let ava = data.picture_src_ava;
      let preview = data.picture_src_preview;
      return ListItem.genItem({ name, type, time, size, path, icon, ava, preview, filter_string, data })
    })
  }

  private extractArticlesByFolder(res: any) {
    let ret = {items: [], isOwner: false, folderUUID: '', folderName: ''}
    if (!res) {
      return ret
    }
    ret.isOwner = res['isOwner'];
    ret.folderUUID = res['folderUUID'];
    ret.folderName = res['folderName'];
    ret.items = res['articlesData'].map((categoryData) => {
      let category = {
        sorder: categoryData['sorder'],
        group_name: categoryData['group_name'],
        group_key: categoryData['group_key'],
        group_image_src: categoryData['group_image_src'],
        articles: categoryData['articles'].map((data) => {
          let articleDTO = JSON.parse(data.data);
          let video_url = ListItem.getVideoUrl(articleDTO.article);

          let name = ListItem.getFrontTitle(articleDTO.article);
          let filter_string = name;
          let type = video_url ? 'article_vs_video' : 'article';
          let time = data.local_mdate * 1000;
          let size = 0;
          let path = '/'+data['uuid'];
          let icon = ListItem.getFrontBannerSrcF(articleDTO.article);
          let ava = ListItem.getFrontBannerSrcF(articleDTO.article);
          let preview = ListItem.getFrontBannerSrcF(articleDTO.article);
          let articleItem = ListItem.genItem({ name, type, time, size, path, icon, ava, preview, filter_string, data })
          return articleItem;
        })
      };
      return category;
    });
    return ret
  }

  private extractArticleItemData(res: any) {
    if (!res) {
      return null
    }
    let ret = {articleItem: null, courseNavigation: null, folderUUID: '', folderName: ''};

    let data = res['articleItem'];
    let articleDTO = JSON.parse(data['data']);
    let video_url = ListItem.getVideoUrl(articleDTO.article);

    let name = ListItem.getFrontTitle(articleDTO.article);
    let filter_string = name;
    let type = video_url ? 'article_vs_video' : 'article';
    let time = data.local_mdate * 1000;
    let size = 0;
    let path = '/'+data.uuid;
    let icon = ListItem.getFrontBannerSrcF(articleDTO.article);
    let ava = ListItem.getFrontBannerSrcF(articleDTO.article);
    let preview = ListItem.getFrontBannerSrcF(articleDTO.article);
    let articleItem = ListItem.genItem({ name, type, time, size, path, icon, ava, preview, filter_string, data })

    ret['articleItem'] = articleItem;
    ret['courseNavigation'] = res['courseNavigation'];
    ret['ownerProfile'] = res['ownerProfile'];
    ret['folderUUID'] = res['folderUUID'];
    ret['folderName'] = res['folderName'];

    return ret;
  }

  private extractSnapshotListData(res: any) {
    if (!res || !res.snapshots) {
      return []
    }
    return res.snapshots.map(name => {
      const file = new File();
      file.type = 'b';
      file.name = he.decode(name);
      file.full_path = (`/${file.name}`).replace(/\/+/g, '/');
      return file
    });
  }

  private extractSnapshotFilesData(res: any, snapshot: string, path: string) {
    if (!res || !res.snapshots) {
      return []
    }
    return res.snapshots.map(({ name, owner, perm, size, time, type }) => {
      const file = new File();
      file.type = type;
      file.name = he.decode(name);
      file.full_path = (`/${snapshot}/${path}/${file.name}`).replace(/\/+/g, '/');
      file.cdate = time * 1000;
      file.size = size;
      file.isSnapshot = true;
      return file
    })
  }
}
